import React, { useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import { cx } from "./Controls";

async function readEntry(entry: any): Promise<File[]> {
  if (entry.isFile) return new Promise((res) => entry.file((f: File) => res([f]), () => res([])));
  if (!entry.isDirectory) return [];
  const reader = entry.createReader();
  const all: File[] = [];
  const readBatch = (): Promise<void> =>
    new Promise((res) =>
      reader.readEntries(async (entries: any[]) => {
        if (!entries.length) return res();
        for (const e of entries) all.push(...(await readEntry(e)));
        await readBatch();
        res();
      }, () => res()),
    );
  await readBatch();
  return all;
}

async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  const items = Array.from(dt.items || []);
  const entries = items.map((i) => (i as any).webkitGetAsEntry?.()).filter(Boolean);
  if (!entries.length) return Array.from(dt.files);
  const out: File[] = [];
  for (const e of entries) out.push(...(await readEntry(e)));
  return out;
}

export function Dropzone({ onFiles, multiple, allowFolder, accept = "image/*", label, children, testId }: {
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  allowFolder?: boolean;
  accept?: string;
  label: string;
  children?: React.ReactNode;
  testId?: string;
}) {
  const [over, setOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const dirRef = useRef<HTMLInputElement>(null);
  const emit = (files: File[]) => {
    const list = files.filter((f) => !f.name.startsWith("."));
    if (list.length) onFiles(multiple ? list : list.slice(0, 1));
  };
  return (
    <div
      className={cx("rounded-xl border-2 border-dashed p-5 text-center transition-colors duration-150", over ? "border-signal bg-signal-soft" : "border-slate-300 bg-slate-50/60 hover:border-slate-400")}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setOver(false);
        emit(await filesFromDrop(e.dataTransfer));
      }}
      data-testid={testId || "dropzone"}
    >
      {children || (
        <div className="flex flex-col items-center gap-2 text-sm text-slate-500">
          <UploadCloud className="h-7 w-7 text-slate-400" />
          <span>{label}</span>
        </div>
      )}
      <div className="mt-3 flex flex-wrap justify-center gap-2">
        <button type="button" className="rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink hover:bg-slate-50" onClick={() => fileRef.current?.click()} data-testid={`${testId || "dropzone"}-pick-files`}>
          {multiple ? "Elegir archivos" : "Elegir archivo"}
        </button>
        {allowFolder && (
          <button type="button" className="rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink hover:bg-slate-50" onClick={() => dirRef.current?.click()} data-testid={`${testId || "dropzone"}-pick-folder`}>
            Elegir carpeta
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" hidden accept={accept} multiple={multiple} onChange={(e) => { emit(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid={`${testId || "dropzone"}-input`} />
      {allowFolder && (
        <input ref={dirRef} type="file" hidden multiple {...({ webkitdirectory: "", directory: "" } as any)} onChange={(e) => { emit(Array.from(e.target.files || [])); e.target.value = ""; }} />
      )}
    </div>
  );
}
